import api from '../api';

const baseURL = '/speaking/v1/practice';

export interface PronunciationAnalysisRequest {
    audio_blob: Blob;
    target_text: string;
    content_type: string;
    exercise_id?: string;
}

export interface PronunciationAnalysisResponse {
    phoneme_scores: Array<{
        phoneme: string;
        target_phoneme: string;
        detected_phoneme: string;
        accuracy_rate: number;
        confidence_score: number;
        start_time: number;
        end_time: number;
        error_type?: string;
        severity?: 'minor' | 'moderate' | 'major';
    }>;
    pronunciation_errors: Array<{
        phoneme: string;
        error_type: string;
        severity: 'minor' | 'moderate' | 'major';
        suggestion: string;
        position: number;
    }>;
    overall_score: number;
    fluency_score: number;
    accuracy_score: number;
    prosody_score: number;
}

export interface GPTFeedbackRequest {
    pronunciation_errors: Array<{
        phoneme: string;
        error_type: string;
        severity: 'minor' | 'moderate' | 'major';
        suggestion: string;
    }>;
    overall_score: number;
    user_text: string;
    target_text: string;
    feedback_tone: 'playful' | 'serious' | 'explanatory' | 'encouraging';
}

export interface GPTFeedbackResponse {
    overall_feedback: string;
    phoneme_specific_suggestions: Array<{
        phoneme: string;
        suggestion: string;
        practice_tip: string;
        example_words: string[];
    }>;
    encouragement_message: string;
    positive_points: string[];
    improvement_areas: string[];
    practice_recommendations: string[];
}

export interface AIAudioComparisonRequest {
    user_audio_url: string;
    target_text: string;
    target_phonemes: string[];
}

export interface AIAudioComparisonResponse {
    reference_audio_url: string;
    phoneme_references: Array<{
        phoneme: string;
        reference_audio_segment: string;
        user_audio_segment: string;
        similarity_score: number;
    }>;
    similarity_analysis: {
        pitch_similarity: number;
        rhythm_similarity: number;
        phoneme_accuracy: number;
        overall_quality: number;
    };
}

export interface AvatarPracticeRequest {
    target_text: string;
    problematic_phonemes: string[];
    user_level: string;
    avatar_type: '2d' | '3d';
    feedback_tone: 'playful' | 'serious' | 'explanatory' | 'encouraging';
}

export interface AvatarPracticeResponse {
    avatar_video_url: string;
    practice_script: string;
    phoneme_breakdown: Array<{
        phoneme: string;
        timing: number;
        mouth_position: string;
        practice_tip: string;
    }>;
    interactive_elements: Array<{
        type: 'highlight' | 'pause' | 'repeat';
        timing: number;
        content: string;
    }>;
}

export interface VectorUpdateRequest {
    user_id: string;
    session_data: {
        pronunciation_errors: Array<{
            phoneme: string;
            error_type: string;
            frequency: number;
        }>;
        accuracy_scores: {
            overall: number;
            phoneme_level: Array<{
                phoneme: string;
                score: number;
            }>;
        };
        practice_time: number;
        improvement_rate: number;
    };
    behavior_vector: {
        learning_style: string;
        preferred_feedback_tone: string;
        progress_rate: number;
        challenge_level: string;
    };
}

class PracticeService {
    // Step 1: STT / Forced Alignment
    async analyzePronunciation(request: PronunciationAnalysisRequest): Promise<PronunciationAnalysisResponse> {
        const formData = new FormData();
        formData.append('audio', request.audio_blob);
        formData.append('target_text', request.target_text);
        formData.append('content_type', request.content_type);

        if (request.exercise_id) {
            formData.append('exercise_id', request.exercise_id);
        }

        const response = await api.post(`${baseURL}/analyze-pronunciation/`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data;
    }

    // Step 2: GPT Feedback Engine
    async generateConversationalFeedback(request: GPTFeedbackRequest): Promise<GPTFeedbackResponse> {
        const response = await api.post(`${baseURL}/generate-feedback/`, request);
        return response.data;
    }

    // Step 3: AI Audio Comparison
    async generateReferenceAudio(request: AIAudioComparisonRequest): Promise<AIAudioComparisonResponse> {
        const response = await api.post(`${baseURL}/generate-reference-audio/`, request);
        return response.data;
    }

    // Step 4: Avatar Generator
    async generateAvatarPractice(request: AvatarPracticeRequest): Promise<AvatarPracticeResponse> {
        const response = await api.post(`${baseURL}/generate-avatar-practice/`, request);
        return response.data;
    }

    // Step 5: Vector Update Engine
    async updateBehaviorVector(request: VectorUpdateRequest): Promise<{ message: string; updated_vector: any }> {
        const response = await api.post(`${baseURL}/update-behavior-vector/`, request);
        return response.data;
    }

    // Combined Practice Flow
    async executePracticeFlow(
        audio_blob: Blob,
        target_text: string,
        exercise_id?: string,
        feedback_tone: 'playful' | 'serious' | 'explanatory' | 'encouraging' = 'encouraging',
        avatar_type: '2d' | '3d' = '2d',
    ): Promise<{
        pronunciation_analysis: PronunciationAnalysisResponse;
        gpt_feedback: GPTFeedbackResponse;
        audio_comparison: AIAudioComparisonResponse;
        avatar_practice: AvatarPracticeResponse;
        vector_update: { message: string; updated_vector: any };
    }> {
        // Step 1: Analyze pronunciation
        const pronunciationAnalysis = await this.analyzePronunciation({
            audio_blob,
            target_text,
            content_type: 'audio/webm',
            exercise_id,
        });

        // Step 2: Generate GPT feedback
        const gptFeedback = await this.generateConversationalFeedback({
            pronunciation_errors: pronunciationAnalysis.pronunciation_errors,
            overall_score: pronunciationAnalysis.overall_score,
            user_text: target_text, // In a real scenario, this would be transcribed text
            target_text,
            feedback_tone,
        });

        // Step 3: Generate reference audio and comparison
        const audioComparison = await this.generateReferenceAudio({
            user_audio_url: URL.createObjectURL(audio_blob), // This would be uploaded to server first
            target_text,
            target_phonemes: pronunciationAnalysis.phoneme_scores.map((p) => p.phoneme),
        });

        // Step 4: Generate avatar practice
        const avatarPractice = await this.generateAvatarPractice({
            target_text,
            problematic_phonemes: pronunciationAnalysis.pronunciation_errors
                .filter((error) => error.severity === 'major' || error.severity === 'moderate')
                .map((error) => error.phoneme),
            user_level: this.determineUserLevel(pronunciationAnalysis.overall_score),
            avatar_type,
            feedback_tone,
        });

        // Step 5: Update behavior vector
        const vectorUpdate = await this.updateBehaviorVector({
            user_id: 'current_user', // Would be retrieved from auth context
            session_data: {
                pronunciation_errors: pronunciationAnalysis.pronunciation_errors.map((error) => ({
                    phoneme: error.phoneme,
                    error_type: error.error_type,
                    frequency: 1, // Would be calculated based on session data
                })),
                accuracy_scores: {
                    overall: pronunciationAnalysis.overall_score,
                    phoneme_level: pronunciationAnalysis.phoneme_scores.map((phoneme) => ({
                        phoneme: phoneme.phoneme,
                        score: phoneme.accuracy_rate,
                    })),
                },
                practice_time: 0, // Would be tracked during session
                improvement_rate: 0, // Would be calculated based on historical data
            },
            behavior_vector: {
                learning_style: 'adaptive', // Would be determined from user behavior
                preferred_feedback_tone: feedback_tone,
                progress_rate: this.calculateProgressRate(pronunciationAnalysis.overall_score),
                challenge_level: this.determineUserLevel(pronunciationAnalysis.overall_score),
            },
        });

        return {
            pronunciation_analysis: pronunciationAnalysis,
            gpt_feedback: gptFeedback,
            audio_comparison: audioComparison,
            avatar_practice: avatarPractice,
            vector_update: vectorUpdate,
        };
    }

    // Helper methods
    private determineUserLevel(overallScore: number): string {
        if (overallScore >= 90) return 'advanced';
        if (overallScore >= 70) return 'intermediate';
        return 'beginner';
    }

    private calculateProgressRate(overallScore: number): number {
        // Simple calculation - in reality this would be more complex
        return Math.min(100, overallScore + 10) / 100;
    }

    // Practice Session Management
    async startPracticeSession(exercise_id?: string): Promise<{ session_id: string; started_at: string }> {
        const response = await api.post(`${baseURL}/start-session/`, { exercise_id });
        return response.data;
    }

    async endPracticeSession(session_id: string, session_data: any): Promise<{ message: string; results: any }> {
        const response = await api.post(`${baseURL}/end-session/${session_id}/`, session_data);
        return response.data;
    }

    // Get practice recommendations
    async getPracticeRecommendations(user_phoneme_data: any[]): Promise<{
        recommended_phonemes: string[];
        practice_exercises: any[];
        difficulty_adjustments: any;
    }> {
        const response = await api.post(`${baseURL}/recommendations/`, { user_phoneme_data });
        return response.data;
    }
}

const practiceService = new PracticeService();
export default practiceService;
